import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, ViewContainerRef, AfterViewInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { DashboardViewService } from 'src/app/services/dashboard.services';
import { DashBoardInterface, DashBoardTypeEnum } from './interfaces/dashboard';
import { GridsterOption } from 'src/app/interfaces/gridster-options';
import { CompactType, DisplayGrid, GridType, GridsterConfig } from 'angular-gridster2';
import { TableCardConfigurationComponent } from './table-card/table-card-configuration/table-card-configuration.component';
import { MiniCardConfigurationComponent } from './mini-card/mini-card-configuration/mini-card-configuration.component';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { UserAuthenticationServices } from 'src/app/services/user-authentication.services';
import { FilterParam } from 'src/app/interfaces/filter-param';
import { FormServices } from 'src/app/services/form.services';
import { DahsBoardFilterConfigurationComponent } from './dashboard-filter-configurator/dashboard-filter-configurator.component';
import { GenericEdit } from 'src/app/interfaces/generic-edit';
import { ChartCardConfigurationComponent } from './chart-card/chart-card-configuration/chart-card-configuration.component';
import { PermissionServices } from 'src/app/services/permission.services';
import { ItensPermission } from 'src/app/interfaces/RolePermission';
import { DashboardFocusService } from 'src/app/services/dashboard-focus.service';
import { GadGetAction } from './interfaces/gadget-events';
import { TabState } from './tab-state';
import { normalizeDashboard, serializeDashboard } from './dashboard-serializer';
import { openGadgetModal } from './gadget-modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';

/*
 * Módulos NG-ZORRO necessários no módulo que declara este componente:
 *   NzTabsModule, NzCollapseModule, NzLayoutModule, NzModalModule, NzFormModule
 *   (via SharedModule: NzButtonModule, NzIconModule, NzInputModule,
 *    NzDividerModule, NzToolTipModule, NzDropDownModule, NzFloatButtonModule)
 * FormsModule + ReactiveFormsModule já eram necessários antes.
 */
@Component({
    selector: 'main-dashboard',
    templateUrl: './main-dashboard.component.html',
    styleUrls: ['./main-dashboard.component.scss'],
    standalone: false,
    // uma instância POR dashboard: dois dashboards vivos (home + editor CRUD)
    // não corrompem o contexto codeCompany/codeContract um do outro
    providers: [DashboardViewService]
})
export class MainDashboardComponent implements OnInit, AfterViewInit {

  showSave = false;
  showFilter = true;
  public dashboard: DashBoardInterface[] = [];

  selectedIndex = 0;

  /** injetado imperativamente pelo simple-datagrid no modo CRUD */
  data: GenericEdit;
  @Output() saving: EventEmitter<{ changing: boolean, keepOpen: boolean }> = new EventEmitter();
  @Input('iddashboard') iddashboard: number = -1;
  /** redimensionamento externo (instance-generic-view) — a diretiva
   *  gridsterAutoResize já cobre; mantido por compatibilidade */
  @Input() events?: Observable<void>;

  /** id interno mutável (INTERNALID retornado pelo save); o @Input fica imutável */
  dashboardId = -1;

  canEdit = true;
  canInsert = true;
  canDelete = true;
  canView = true;
  insertAlterMode = false;
  evento: string;
  formAction: FormGroup;
  conditionCodeCompany: string = "0=0";

  /** estado de UI por aba — O(1), referências estáveis, nunca serializado */
  private tabs = new Map<string, TabState>();
  /** serializa gravações concorrentes (dois cliques rápidos não geram saves fora de ordem) */
  private saveQueue: Promise<unknown> = Promise.resolve();

  constructor(
    private dvs: DashboardViewService,
    private message: NzMessageService,
    private modal: NzModalService,
    private fb: FormBuilder,
    private uas: UserAuthenticationServices,
    private fs: FormServices,
    private ps: PermissionServices,
    private dfs: DashboardFocusService,
    private destroyRef: DestroyRef,
    public viewContainerRef: ViewContainerRef,
  ) { }

  // ─────────────────────────── estado por aba ───────────────────────────

  /** acessor idempotente — cria o TabState na primeira referência */
  state(tabName: string): TabState {
    let s = this.tabs.get(tabName);
    if (!s) {
      s = new TabState(tabName, this.fb);
      this.tabs.set(tabName, s);
    }
    return s;
  }

  /** edit mode com gate de permissão (usado pelo template) */
  editMode(tabName: string): boolean {
    return this.canEdit && this.state(tabName).editMode;
  }

  /** modo avançado (JSON) com gate de permissão */
  showJSON(tabName: string): boolean {
    return this.canEdit && this.state(tabName).showJSON;
  }

  // ─────────────────────────── ciclo de vida ───────────────────────────

  ngOnInit(): void {

    if (this.data != null) {

      this.insertAlterMode = true;
      this.evento = this.data.evento;
      this.dvs.codeCompany = this.data.row['CODECOMPANY'];
      this.dvs.codeContract = this.data.row['CODECONTRACT'];
      this.dvs.name = this.data.row['NAME'];

      this.dvs.idUser = this.uas.user.iduser;
      this.dvs.codeCompanyList = this.uas.getCompanies();
      this.dvs.codeContractList = this.uas.getContracts();
      this.dashboardId = this.data.row['INTERNALID'];

      this.buildFormAction();

      if (this.evento == 'editando' && this.dvs.codeContract != null) {
        this.onChangeCodeContract();
      }

    } else {
      this.dvs.idUser = this.uas.user.iduser;
      this.dvs.codeCompanyList = this.uas.getCompanies();
      this.dvs.codeContractList = this.uas.getContracts();

      this.buildFormAction();

      this.dashboardId = (this.iddashboard == null || this.iddashboard === -1)
        ? this.uas.user.iddashboard
        : this.iddashboard;
    }

    const perm: ItensPermission = this.ps.checkPermission("Mktpdashboard");
    this.canDelete = perm.canDelete;
    this.canEdit = perm.canEdit;
    this.canInsert = perm.canInsert;
    this.canView = perm.canView;
  }

  private buildFormAction(): void {
    this.formAction = this.fb.group({
      INTERNALID: new FormControl<number>({ value: null, disabled: true }),
      NAME: new FormControl<string>(this.dvs.name, Validators.required),
      CODECOMPANY: new FormControl<number>({ value: this.dvs.codeCompany, disabled: false }),
      CODECONTRACT: new FormControl<number>({ value: this.dvs.codeContract, disabled: false }),
    });

    this.formAction.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.showSave = true;
        if (value['CODECOMPANY'] != null) {
          this.dvs.codeCompany = value['CODECOMPANY'];
        }
        if (value['CODECONTRACT'] != null) {
          this.dvs.codeContract = value['CODECONTRACT'];
        }
        if (value['NAME'] != null) {
          this.dvs.name = value['NAME'];
        }
      });
  }

  ngAfterViewInit(): void {
    this.load();

    if (this.events != null) {
      this.events
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.resizeAll());
    }
  }

  private load(): void {
    if (this.dashboardId == null || this.dashboardId === 0 || this.evento === 'inserindo') {
      return;
    }

    this.dvs.getDashBoardConfiguration(this.dashboardId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(raw => {
        this.dashboard = normalizeDashboard(raw, () => this.createDefaultOption());
        this.dashboard.forEach(tab => {
          tab.iddashboard = this.dashboardId;
          this.attachTabRuntime(tab);
          this.state(tab.tabName);
        });

        if (this.formAction != null) {
          this.formAction.get("CODECOMPANY").setValue(this.dvs.codeCompany, { emitEvent: false });
          this.formAction.get("CODECONTRACT").setValue(this.dvs.codeContract, { emitEvent: false });
          this.formAction.get("NAME").setValue(this.dvs.name, { emitEvent: false });
          this.showSave = false;
        }

        this.selectedIndex = 0;
        this.dashboard.forEach(tab => this.loadFilter(tab));
        this.dfs.setFocus(true);
      });
  }

  /** callbacks runtime das options (nunca persistidos — o serializer os remove) */
  private attachTabRuntime(tab: DashBoardInterface): void {
    if (tab.options == null) {
      return;
    }
    tab.options.itemChangeCallback = () => {
      this.state(tab.tabName).dirty = true;
    };
  }

  private resizeAll(): void {
    this.dashboard?.forEach(tab => {
      tab.options?.api?.optionsChanged?.();
      tab.options?.api?.resize?.();
    });
  }

  // ─────────────────────────── filtros por aba ───────────────────────────

  /**
   * Carrega a preferência de filtro da aba e SEMPRE emite algo no canal
   * da aba (form vivo populado, ou null) — gadgets carregam mesmo sem
   * filtro, e assinantes tardios recebem via replay.
   */
  private async loadFilter(tab: DashBoardInterface): Promise<void> {
    const state = this.state(tab.tabName);
    const resourceid = `dashboard_filter_${this.dashboardId}_${tab.tabName}`;
    try {
      const preference = await this.dvs.loadPreference(this.uas.user.iduser, resourceid);
      if (preference != null) {
        state.lastFilter = preference;
        this.fs.refreshFormWithCreationObject(state.filterForm, JSON.parse(preference) as Object);
        state.emitFilter(state.filterForm);
      } else {
        state.emitFilter(null);
      }
    } catch {
      state.emitFilter(null);
    }
  }

  /** aplica o filtro da aba: salva a preferência e notifica SÓ os gadgets desta aba */
  async notifyGadGets(tab: DashBoardInterface): Promise<void> {
    const state = this.state(tab.tabName);
    const resourceid = `dashboard_filter_${this.dashboardId}_${tab.tabName}`;
    const preference = JSON.parse(JSON.stringify(state.filterForm.value));
    try {
      await this.dvs.saveUserConfig(this.uas.user.iduser, resourceid, preference);
    } catch {
      // erro já notificado pelo service; o filtro ainda é aplicado localmente
    }
    state.emitFilter(state.filterForm);
  }

  toggleFilter(tabName: string): void {
    const state = this.state(tabName);
    state.isFilterExpanded = !state.isFilterExpanded;
    // o resize do gridster durante a animação do sider é responsabilidade
    // da diretiva gridsterAutoResize (ResizeObserver)
  }

  // ─────────────────────────── persistência ───────────────────────────

  /**
   * Ponto único de gravação: serializa chamadas concorrentes, atualiza o
   * INTERNALID e dá o feedback. Retorna false em falha (o service já
   * exibiu o toast de erro) — o chamador decide o rollback.
   */
  private persist(successMsg = 'Dashboard alterado com sucesso.'): Promise<boolean> {
    const run = async (): Promise<boolean> => {
      const r = await this.dvs.saveDashBoard(this.dashboardId, this.dashboard);
      this.dashboardId = r.INTERNALID;
      this.message.success(successMsg);
      return true;
    };
    const result = this.saveQueue.then(run, run).catch(() => false);
    this.saveQueue = result;
    return result;
  }

  async salvar(): Promise<void> {
    if (!this.checkError()) {
      return;
    }
    const ok = await this.persist();
    if (ok) {
      this.showSave = false;
    }
  }

  voltar(): void {
    this.saving.emit({ changing: true, keepOpen: false });
  }

  checkError(): boolean {
    if (this.insertAlterMode && this.formAction?.invalid) {
      this.message.error(
        this.fs.getFormErrors(this.formAction, [
          { colName: 'CODECONTRACT', colDesc: 'Cód. Contrato' },
          { colName: 'CODECOMPANY', colDesc: 'Cód. Contexto ( Company )' },
          { colName: 'NAME', colDesc: 'Nome' },
        ])
      );
      return false;
    }
    return true;
  }

  // ─────────────────────────── edição de aba ───────────────────────────

  /** abre/fecha o leque de ações; fechar em modo edição = cancelar alterações */
  clickMenu(tabName: string): void {
    const state = this.state(tabName);
    if (state.openMenu && state.editMode) {
      state.editMode = false;
      state.dirty = false;
      const index = this.dashboard.findIndex(o => o.tabName === tabName);
      if (index !== -1 && state.oldJSON != null) {
        this.restoreTab(index, state.oldJSON);
      } else if (index !== -1) {
        this.changeEditModeGrid(this.dashboard[index], false);
      }
    }
    state.openMenu = !state.openMenu;
  }

  async actionEdit(tabName: string): Promise<void> {
    if (!this.checkError()) {
      return;
    }
    const state = this.state(tabName);
    const tab = this.dashboard.find(d => d.tabName === tabName);
    if (tab == null) {
      return;
    }

    if (state.editMode) {
      // check: encerra a edição e grava
      this.changeEditModeGrid(tab, false);
      state.editMode = false;
      state.showJSON = false;
      const ok = await this.persist();
      if (!ok && state.oldJSON != null) {
        const index = this.dashboard.findIndex(d => d.tabName === tabName);
        if (index !== -1) {
          this.restoreTab(index, state.oldJSON);
        }
      }
      state.dirty = false;
    } else {
      // snapshot LIMPO (sem runtime) para restaurar no cancelamento
      state.oldJSON = serializeDashboard([tab])[0] ?? null;
      state.editMode = true;
      this.changeEditModeGrid(tab, true);
    }
  }

  private restoreTab(index: number, snapshot: DashBoardInterface): void {
    const [restored] = normalizeDashboard([snapshot], () => this.createDefaultOption());
    if (restored != null) {
      this.attachTabRuntime(restored);
      this.dashboard[index] = restored;
    }
  }

  private changeEditModeGrid(tab: DashBoardInterface, mode: boolean): void {
    const options = tab?.options;
    if (options == null) {
      return;
    }
    options.setGridSize = mode;
    options.swap = mode;
    options.draggable = { ...options.draggable, enabled: mode };
    options.resizable = { ...options.resizable, enabled: mode };
    options.pushItems = mode;
    options.api?.optionsChanged?.();
  }

  addNewTab(): void {
    if (!this.checkError()) {
      return;
    }

    const newTab: DashBoardInterface = {
      iddashboard: this.dashboardId,
      filter: [],
      gadGets: [],
      tabName: Date.now().toString(),
      options: this.createDefaultOption(),
      tabTitle: "Nova Tab",
      title: "Meu Título"
    };
    this.attachTabRuntime(newTab);
    this.dashboard.push(newTab);
    const state = this.state(newTab.tabName);
    state.emitFilter(null); // aba nova não tem filtro — gadgets futuros carregam

    this.persist('Aba criada.').then(ok => {
      if (!ok) {
        const index = this.dashboard.indexOf(newTab);
        if (index !== -1) {
          this.dashboard.splice(index, 1);
        }
        this.tabs.delete(newTab.tabName);
      } else {
        this.selectedIndex = this.dashboard.length - 1;
      }
    });
  }

  closeTab(tabName: string): void {
    if (!this.canDelete) {
      this.message.warning('Você não tem permissão para excluir abas.');
      return;
    }
    if (!this.checkError()) {
      return;
    }

    this.modal.confirm({
      nzTitle: 'Exclusão de Aba',
      nzContent: 'Deseja realmente excluir esta aba?',
      nzOkText: 'Excluir',
      nzOkDanger: true,
      nzCancelText: 'Cancelar',
      nzOnOk: async () => {
        const index = this.dashboard.findIndex(d => d.tabName === tabName);
        if (index === -1) {
          return;
        }
        const removed = this.dashboard.splice(index, 1);
        const ok = await this.persist('Aba excluída.');
        if (!ok) {
          this.dashboard.splice(index, 0, ...removed);
        } else {
          this.tabs.delete(tabName);
          this.selectedIndex = 0;
        }
      },
    });
  }

  // ─────────────────────────── gadgets ───────────────────────────

  openCreator(evento: DashBoardTypeEnum | string, tabName: string): void {

    const state = this.state(tabName);
    state.openMenu = false;

    if (state.editMode) {
      const tab = this.dashboard.find(d => d.tabName === tabName);
      if (tab != null) {
        this.changeEditModeGrid(tab, false);
      }
      state.editMode = false;
    }

    const creators = {
      [DashBoardTypeEnum.minicard]: MiniCardConfigurationComponent,
      [DashBoardTypeEnum.table]: TableCardConfigurationComponent,
      [DashBoardTypeEnum.chart]: ChartCardConfigurationComponent,
    };
    const component = creators[evento];
    if (component == null) {
      return;
    }

    const modalRef = openGadgetModal(this.modal, this.viewContainerRef, component, {
      item: null,
      name: null,
      tabName: tabName,
      idDashBoard: this.dashboardId,
      type: evento
    });

    modalRef.afterClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result != null) {
          this.addingGadGet(result, tabName);
        }
      });
  }

  addingGadGet(item: unknown, tabName: string): void {

    if (!this.checkError()) {
      return;
    }

    const tab = this.dashboard.find(dash => dash.tabName === tabName);
    if (tab == null || item == null) {
      return;
    }
    tab.gadGets.push(item as DashBoardInterface['gadGets'][number]);

    this.persist('Componente adicionado.').then(ok => {
      if (!ok) {
        tab.gadGets.pop();
      }
    });
  }

  deletingGadGet(evento: GadGetAction): void {

    if (!this.canDelete) {
      this.message.warning('Você não tem permissão para excluir componentes.');
      return;
    }

    this.modal.confirm({
      nzTitle: 'Exclusão de Componente',
      nzContent: 'Deseja excluir o componente?',
      nzOkText: 'Excluir',
      nzOkDanger: true,
      nzCancelText: 'Cancelar',
      nzOnOk: async () => {
        const tab = this.dashboard.find(d => d.tabName === evento.tabName);
        if (tab == null) {
          return;
        }
        const index = tab.gadGets.findIndex(i => i.name === evento.name && i.type === evento.type);
        if (index === -1) {
          return;
        }
        const removed = tab.gadGets.splice(index, 1);
        const ok = await this.persist('Componente excluído.');
        if (!ok) {
          tab.gadGets.splice(index, 0, ...removed);
        }
      },
    });
  }

  openFilter(item: DashBoardInterface): void {

    if (!this.checkError()) {
      return;
    }

    // o configurador trabalha sobre uma CÓPIA e devolve o resultado no
    // close — cancelar não exige mais restaurar oldFilter no pai
    const modalRef = openGadgetModal(this.modal, this.viewContainerRef, DahsBoardFilterConfigurationComponent, {
      filter: item.filter
    });

    modalRef.afterClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: FilterParam[] | undefined) => {
        if (result == null) {
          return;
        }
        const oldFilter = item.filter;
        item.filter = JSON.parse(JSON.stringify(result)) as FilterParam[];
        this.showFilter = false;
        this.persist().then(ok => {
          if (!ok) {
            item.filter = oldFilter;
          }
          this.showFilter = true;
        });
      });
  }

  // ─────────────────────────── modo avançado (JSON) ───────────────────────────

  showJSONClick(item: DashBoardInterface, mode: boolean): void {
    const state = this.state(item.tabName);

    if (mode) {
      // mostra exatamente o shape persistido (sem runtime)
      state.jsonControl.setValue(JSON.stringify(serializeDashboard([item])[0], null, "\t"));
      state.jsonControl.markAsPristine();
      state.showJSON = true;
      return;
    }

    if (state.jsonControl.pristine) {
      state.showJSON = false;
      return;
    }

    try {
      const parsed = JSON.parse(state.jsonControl.value);
      const [normalized] = normalizeDashboard([parsed], () => this.createDefaultOption());

      if (normalized == null) {
        this.message.error("JSON inválido para aba. Cole apenas o JSON da aba atual.");
        return;
      }

      const index = this.dashboard.findIndex(a => a.tabName === item.tabName);
      if (index === -1) {
        return;
      }
      this.attachTabRuntime(normalized);
      this.dashboard[index] = normalized;
      state.showJSON = false;
    } catch (e) {
      this.message.error("JSON inválido.");
    }
  }

  // ─────────────────────────── gridster ───────────────────────────

  private createDefaultOption(): GridsterConfig {
    const options = new GridsterOption().get();
    options.compactType = CompactType.CompactUp;
    options.gridType = GridType.Fit;
    options.displayGrid = DisplayGrid.OnDragAndResize;
    options.maxCols = 10;
    options.minCols = 10;
    options.setGridSize = false;
    options.swap = false;
    options.draggable.enabled = false;
    options.resizable.enabled = false;
    options.pushItems = false;
    options.rowHeightRatio = 1;
    return options;
  }

  onChangeCodeContract(): void {
    const value = this.formAction?.get('CODECONTRACT')?.value;
    this.conditionCodeCompany = value != null ? `CODECONTRACT = ${value}` : "0=0";
  }
}
